import {
  Fragment,
  useState,
  useRef,
  useMemo,
  Suspense,
  useEffect,
  useCallback,
  useContext,
  createContext,
} from "react";
import {
  Canvas,
  extend,
  useThree,
  useFrame,
  useLoader,
  useResource,
} from "react-three-fiber";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { ImprovedNoise } from "three/examples/jsm/math/ImprovedNoise.js";

import chroma from "chroma-js";
import SimplexNoise from "simplex-noise";

import magicFrag from "./magic.frag.js";
import magicVert from "./magic.vert.js";

import blobFrag from "./blob.frag.js";
import blobVert from "./blob.vert.js";

import envMap from "./envmap.jpg";

import { parseInputReport } from "./ds4";

let controllerData = {};
let midiData = {};

extend({ OrbitControls });

const DebugContext = createContext(false);

function generateHeight(width, height) {
  const size = width * height,
    data = new Uint8Array(size),
    perlin = new ImprovedNoise(),
    z = Math.random() * 100;

  let quality = 1;

  for (let j = 0; j < 4; j++) {
    for (let i = 0; i < size; i++) {
      const x = i % width,
        y = ~~(i / width);
      data[i] += Math.abs(
        perlin.noise(x / quality, y / quality, z) * quality * 1.75
      );
    }

    quality *= 5;
  }

  return data;
}

function getSphereCoordinates(u, v) {
  const lon = ((u * 360 - 180) * Math.PI) / 180,
    lat = ((v * 180 - 90) * Math.PI) / 180;

  return [
    (Math.cos(lat) * Math.cos(lon) + 1) / 2,
    (Math.sin(lat) + 1) / 2,
    (Math.cos(lat) * Math.sin(lon) + 1) / 2,
  ];
}

const generateHeightmap = (width, height, scale) => {
  const { noise } = new ImprovedNoise();
  const heightmap = new Uint8ClampedArray(width * height * 4);
  const map = new Uint8ClampedArray(width * height * 4);

  const simplex = new SimplexNoise();

  for (let row = 0; row < height; row++) {
    for (let column = 0; column < width; column++) {
      const value =
        simplex.noise2D((row / height) * 2, (column / width) * 2) * 0.5 + 0.5;

      const [r, g, b] = scale(value).rgb();

      const index = (column + row * width) * 4;
      heightmap[index] = value * 255;
      heightmap[index + 1] = value * 255;
      heightmap[index + 2] = value * 255;
      heightmap[index + 3] = 255;

      map[index] = r;
      map[index + 1] = g;
      map[index + 2] = b;
      map[index + 3] = 255;

      // const index = (column + row * width) * 4;
      // var r = simplex.noise2D(column / 256, row / 256) * 0.5 + 0.5;
      //   data[index + 0] = r * 255;
      //   data[index + 1] = r * 255;
      //   data[index + 2] = r * 255;
      //   data[index + 3] = 255;
    }
  }

  return [heightmap, map];
};

const Scene = ({ materialProperties, uniforms }) => {
  const {
    camera,
    scene,
    gl: { domElement },
  } = useThree();

  // const texture = new DataTexture(material.heightmap, 256, 256, RGBAFormat);
  // texture.wrapS = ClampToEdgeWrapping;
  // texture.wrapT = ClampToEdgeWrapping;

  // const map = new DataTexture(material.colormap, 256, 256, RGBAFormat);

  const shaderRef = useRef();

  useFrame(() => {
    // time.value += uniforms.speed / 100;
    depth.value = uniforms.depth;
    distortion.value = uniforms.distortion;
    scale.value = uniforms.scale * velocity;

    // scale.value = uniforms.scale;

    velocity -= 0.03;
    velocity = Math.max(0, velocity);

    // console.log(velocity);

    // time.value += (midiData[0] === undefined ? 0.5 : midiData[0]) / 100;
    // depth.value = midiData[1] * 10;
    // distortion.value = midiData[2] * 10;
    // scale.value = midiData[3] * 2;

    // camera.rotation.z += 0.01;

    // console.log(midiData);

    // scale.value = controllerData.axes?.l2 + 0.5 || 0;
    // distortion.value = controllerData.axes?.r2 * 10 || 1;

    // console.log(uniforms.depth)
    // depth.value = uniforms.depth;
  });

  let time = useMemo(() => ({ value: 0 }), []);
  let depth = useMemo(() => ({ value: uniforms.depth }), []);
  let distortion = useMemo(() => ({ value: uniforms.distortion }), []);
  let scale = useMemo(() => ({ value: uniforms.scale }), []);

  const onBeforeCompile = useCallback((e) => {
    const blobNoise = `
#define GLSLIFY 1
// #pragma glslify: import(\'./noise.glsl\')
// #pragma glslify: cnoise3 = require(glsl-noise/classic/3d)
// #pragma glslify: cnoise3 = require(glsl-noise/simplex/3d)
//
// GLSL textureless classic 3D noise "cnoise",
// with an RSL-style periodic variant "pnoise".
// Author:  Stefan Gustavson (stefan.gustavson@liu.se)
// Version: 2011-10-11
//
// Many thanks to Ian McEwan of Ashima Arts for the
// ideas for permutation and gradient selection.
//
// Copyright (c) 2011 Stefan Gustavson. All rights reserved.
// Distributed under the MIT license. See LICENSE file.
// https://github.com/ashima/webgl-noise
//

vec3 mod289(vec3 x)
{
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x)
{
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x)
{
  return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r)
{
  return 1.79284291400159 - 0.85373472095314 * r;
}

vec3 fade(vec3 t) {
  return t*t*t*(t*(t*6.0-15.0)+10.0);
}

// Classic Perlin noise, periodic variant
float pnoise(vec3 P, vec3 rep)
{
  vec3 Pi0 = mod(floor(P), rep); // Integer part, modulo period
  vec3 Pi1 = mod(Pi0 + vec3(1.0), rep); // Integer part + 1, mod period
  Pi0 = mod289(Pi0);
  Pi1 = mod289(Pi1);
  vec3 Pf0 = fract(P); // Fractional part for interpolation
  vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;

  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);

  vec4 gx0 = ixy0 * (1.0 / 7.0);
  vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);

  vec4 gx1 = ixy1 * (1.0 / 7.0);
  vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);

  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
  g000 *= norm0.x;
  g010 *= norm0.y;
  g100 *= norm0.z;
  g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
  g001 *= norm1.x;
  g011 *= norm1.y;
  g101 *= norm1.z;
  g111 *= norm1.w;

  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);

  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
  return 2.2 * n_xyz;
}

uniform float time;
uniform float radius;
uniform float distort;
uniform float frequency;
uniform float surfaceDistort;
uniform float surfaceFrequency;
uniform float surfaceTime;
uniform float numberOfWaves;
uniform float fixNormals;
uniform float surfacePoleAmount;
uniform float gooPoleAmount;

uniform int depth;
uniform float scale;

#define M_PI 3.1415926538
#define NOISE_PERIOD 10. // avoid noise artifacts after long duration

float f(vec3 point) {

    // !!! START
    float scale = scale;
float dist = distort;
  int n = depth;

  vec3 p = vec3(point.xy, time) * scale;
  

  float x = sin((p[0] + p[1] + p[2]) * 5.0);
  float y = cos((-p[0] + p[1] - p[2]) * 5.0);
  float z = -cos((-p[0] - p[1] + p[2]) * 5.0);

  if (n > 0) {
    x *= dist;
    y *= dist;
    z *= dist;
    y = -cos(x - y + z);
    y *= dist;

    if (n > 1) {
      x = cos(x - y - z);
      x *= dist;

      if (n > 2) {
        z = sin(-x - y - z);
        z *= dist;

        if (n > 3) {
          x = -cos(-x + y - z);
          x *= dist;

          if (n > 4) {
            y = -sin(-x + y + z);
            y *= dist;

            if (n > 5) {
              y = -cos(-x + y + z);
              y *= dist;

              if (n > 6) {
                x = cos(x + y + z);
                x *= dist;

                if (n > 7) {
                  z = sin(x + y - z);
                  z *= dist;

                  if (n > 8) {
                    x = -cos(-x - y + z);
                    x *= dist;

                    if (n > 9) {
                      y = -sin(x - y + z);
                      y *= dist;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  if (dist != 0.0) {
    dist *= 2.0;
    x /= dist;
    y /= dist;
    z /= dist;
  }

  return 0.5 - (x + y + z)/3.;
    // !!! END

    float yPos = smoothstep(-1., 1., point.y);
    float amount = sin(yPos * M_PI);
    float wavePoleAmount = mix(amount * 1.0, 1.0, surfacePoleAmount);
    float gooPoleAmount = mix(amount * 1.0, 1.0, gooPoleAmount);

    // blob noise
    float goo = pnoise(vec3(point / (frequency) + mod(time, NOISE_PERIOD)), vec3(NOISE_PERIOD)) * pow(distort, 2.0);

    // wave noise
    float surfaceNoise = pnoise(vec3(point / (surfaceFrequency) + mod(surfaceTime, NOISE_PERIOD)), vec3(NOISE_PERIOD));
    float waves = (point.x * sin((point.y+surfaceNoise)*M_PI*numberOfWaves) + point.z * cos((point.y+surfaceNoise)*M_PI*numberOfWaves)) * 0.01 * pow(surfaceDistort, 2.0);

    // combined noise
    return waves * wavePoleAmount + goo * gooPoleAmount;
}

vec3 orthogonal(vec3 v) {
    return normalize(abs(v.x) > abs(v.z) ? vec3(-v.y, v.x, 0.0) : vec3(0.0, -v.z, v.y));
}

  `;

    const blobDisplacement = `
#define GLSLIFY 1
// float displacement = f(position);
vec3 displacedPosition = position + normalize(normal) * f(position);
vec3 displacedNormal = normalize(normal);

// gen new normals
// https://discourse.threejs.org/t/calculating-vertex-normals-after-displacement-in-the-vertex-shader/16989
if (fixNormals == 1.0) {
    float offset = .5 / 512.;
    vec3 tangent = orthogonal(normal);
    vec3 bitangent = normalize(cross(normal, tangent));
    vec3 neighbour1 = position + tangent * offset;
    vec3 neighbour2 = position + bitangent * offset;
    vec3 displacedNeighbour1 = neighbour1 + normal * f(neighbour1);
    vec3 displacedNeighbour2 = neighbour2 + normal * f(neighbour2);

    // https://i.ya-webdesign.com/images/vector-normals-tangent-16.png
    vec3 displacedTangent = displacedNeighbour1 - displacedPosition;
    vec3 displacedBitangent = displacedNeighbour2 - displacedPosition;

    // https://upload.wikimedia.org/wikipedia/commons/d/d2/Right_hand_rule_cross_product.svg
    displacedNormal = normalize(cross(displacedTangent, displacedBitangent));
}
  `;

    e.uniforms.time = time;
    e.uniforms.distort = distortion;
    e.uniforms.depth = depth;
    e.uniforms.scale = scale;

    e.uniforms.radius = {
      value: 3,
    };
    e.uniforms.frequency = {
      value: 0.3,
    };
    e.uniforms.surfaceFrequency = {
      value: 3.5,
    };
    e.uniforms.surfaceTime = {
      value: 0,
    };
    e.uniforms.surfaceSpeed = {
      value: 1,
    };
    e.uniforms.numberOfWaves = {
      value: 10,
    };
    e.uniforms.fixNormals = {
      value: 1,
    };
    e.uniforms.surfacePoleAmount = {
      value: 1,
    };
    e.uniforms.gooPoleAmount = {
      value: 1,
    };

    // Insert vec3 magic()
    e.vertexShader = blobNoise.concat(e.vertexShader);

    // Prepend void main()
    e.vertexShader = e.vertexShader.replace(
      "void main() {",
      "void main() {".concat(blobDisplacement)
    );

    // Displacement jazz
    e.vertexShader = e.vertexShader.replace(
      "#include <displacementmap_vertex>",
      "transformed = displacedPosition;"
    );
    e.vertexShader = e.vertexShader.replace(
      "#include <defaultnormal_vertex>",
      THREE.ShaderChunk.defaultnormal_vertex.replace(
        "vec3 transformedNormal = objectNormal;",
        "vec3 transformedNormal = displacedNormal;"
      )
    );

    // console.log(blobFrag);

    // e.fragmentShader = blobFrag;

    return e;
  });

  // const args = useMemo(() => ({
  //   uniforms: THREE.UniformsUtils.merge([
  //     THREE.ShaderLib.physical.uniforms,
  //     {
  //       time: { type: "f", value: 0 },
  //       scale: { type: "f", value: uniforms.scale },
  //       distortion: { type: "f", value: uniforms.distortion },
  //       depth: { type: "i", value: uniforms.depth },
  //       color1: { type: "v3", value: uniforms.color1 },
  //       color2: { type: "v3", value: uniforms.color2 },
  //       color3: { type: "v3", value: uniforms.color3 },
  //       roughness: { type: "f", value: 0 },
  //       reflectivity: { type: "f", value: 1 },
  //       // transmission: { type: "f", value: .3 },
  //       // shininess: { type: "f", value: 100. },
  //       // specular: { value: new THREE.Color('white') },
  //     },
  //   ]),
  //   fragmentShader: magicFrag,
  //   vertexShader: magicVert,
  // }));

  const envMapTexture = useEquirectangularHDR(envMap);

  return (
    <Fragment>
      <ambientLight intensity={0.4} />

      <Spotlight intensity={5} color="#F3C278" position={[5, 0.07, 1.93]} />
      <Spotlight
        intensity={5}
        color={uniforms.spotlight_2}
        position={[-2.73, -6.6, 5.7]}
      />
      <Spotlight
        intensity={5}
        color={uniforms.spotlight_3}
        position={[1.2, 2.6, 4.6]}
      />

      <mesh ref={shaderRef}>
        <sphereGeometry attach="geometry" args={[1, 2048, 2048]} />

        {/* <primitive attach="material" obj={material} uniforms={uniforms} /> */}

        {
          <meshPhysicalMaterial
            attach="material"
            color={uniforms.spotlight_1}
            roughness={uniforms.roughness}
            metalness={uniforms.metalness}
            reflectivity={1}
            envMap={envMapTexture}
            envMapIntensity={1}
            onBeforeCompile={onBeforeCompile}
            precision="lowp"
          />
        }

        {/* (
          <shaderMaterial
            attach="material"
            ref={shaderRef}
            args={[args]}
            roughness={0}
            uniforms-scale-value={uniforms.scale}
            uniforms-distortion-value={uniforms.distortion}
            uniforms-depth-value={uniforms.depth}
            lights={true}
          />
        ) */}
      </mesh>
      <orbitControls args={[camera, domElement]} />
    </Fragment>
  );
};

const Spotlight = ({ color, position, intensity }) => {
  const spotlight = useResource();
  const debug = useContext(DebugContext);

  return (
    <Fragment>
      <spotLight
        ref={spotlight}
        intensity={intensity}
        color={color}
        position={position}
        angle={Math.PI / 3}
      />
      {debug && spotlight.current && (
        <spotLightHelper args={[spotlight.current]} />
      )}
    </Fragment>
  );
};

const useEquirectangularHDR = (url: string) => {
  const { gl } = useThree();
  const pmremGenerator = new THREE.PMREMGenerator(gl);
  pmremGenerator.compileEquirectangularShader();

  const hdrEquirect = useLoader(THREE.TextureLoader, url);

  const hdrCubeRenderTarget = pmremGenerator.fromEquirectangular(hdrEquirect);
  hdrEquirect.dispose();
  pmremGenerator.dispose();

  return hdrCubeRenderTarget.texture;
};

const generateMaterial = () => {
  const scale = chroma.scale([chroma.random(), chroma.random()]);
  const [heightmap, map] = generateHeightmap(256, 256, scale);

  return {
    roughness: Math.random(),
    metalness: Math.random(),
    displacementScale: Math.random(),
    displacementBias: Math.random(),
    heightmap: heightmap,
    colormap: map,
    startColor: scale(0),
    endColor: scale(1),
  };
};

const MaterialData = ({ material, handleGenerate, handleChange }) => (
  <div style={{ position: "absolute", top: 32, right: 32 }}>
    <button onClick={handleGenerate}>Regenerate</button>

    {Object.keys(material)
      .filter((property) => material[property] !== null)
      .map((property) => (
        <div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <strong>{property}</strong>
            {typeof material[property] === "number" && (
              <span style={{ marginLeft: "auto", fontFamily: "Menlo" }}>
                {material[property].toFixed(4)}
              </span>
            )}
          </div>
          <input
            style={{ width: "100%", display: "block" }}
            type="range"
            min={0}
            max={1}
            step={0.0001}
            value={material[property]}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              handleChange({
                ...material,
                [property]: value,
              });
            }}
          />
        </div>
      ))}

    <pre>
      {JSON.stringify(
        {
          ...material,
          heightmap: "Uint8ClampedArray[65536]",
          colormap: "Uint8ClampedArray[65536]",
        },
        null,
        2
      )}
    </pre>
  </div>
);

const UniformControls = ({ uniforms, handleChange }) => {
  const [pins, setPins] = useState([]);

  return (
    <div
      style={{
        position: "absolute",
        top: 32,
        right: 32,
        width: 256,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 14 }}>
          {[...Array(6)]
            .map(() => Math.floor(Math.random() * 16).toString(16))
            .join("")}
        </span>
        <button
          style={{ margin: "0 0 0 auto" }}
          onClick={() => handleChange(generateUniforms())}
        >
          Regenerate
        </button>
      </div>

      <div>
        {properties.map((property) => {
          switch (property.type) {
            case "f":
            case "i":
              return (
                <div key={property.name} style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 4,
                    }}
                  >
                    <strong style={{ textTransform: "capitalize" }}>
                      {property.name}
                    </strong>
                    <span style={{ marginLeft: "auto" }}>
                      {uniforms[property.name].toFixed(4)}
                    </span>
                  </div>
                  <input
                    style={{ width: "100%", display: "block" }}
                    type="range"
                    min={property.min}
                    max={property.max}
                    step={property.type === "f" ? 0.0001 : 1}
                    value={uniforms[property.name]}
                    onChange={(e) => {
                      const value =
                        property.type === "f"
                          ? parseFloat(e.target.value)
                          : parseInt(e.target.value);
                      handleChange({
                        ...uniforms,
                        [property.name]: value,
                      });
                    }}
                  />
                </div>
              );
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
};

const properties = [
  {
    name: "speed",
    type: "f",
    min: 0,
    max: 1,
    generate: () => 0.5,
  },
  {
    name: "scale",
    type: "f",
    min: 0,
    max: 2,
    generate: () => Math.random(),
  },
  {
    name: "distortion",
    type: "f",
    min: 0,
    max: 10,
    generate: () => Math.random() * 3,
  },
  {
    name: "depth",
    type: "i",
    min: 0,
    max: 10,
    generate: () => Math.floor(Math.random() * 4),
  },
  {
    name: "metalness",
    type: "f",
    min: 0,
    max: 2,
    generate: () => Math.random(),
  },
  {
    name: "roughness",
    type: "f",
    min: 0,
    max: 1,
    generate: () => Math.random(),
  },
  {
    name: "spotlight_1",
    type: "color",
    generate: () => new THREE.Color(Math.random() * 0xffffff),
  },
  {
    name: "spotlight_2",
    type: "color",
    generate: () => new THREE.Color(Math.random() * 0xffffff),
  },
  {
    name: "spotlight_3",
    type: "color",
    generate: () => new THREE.Color(Math.random() * 0xffffff),
  },
];

const generateUniforms = () =>
  properties.reduce((acc, val) => ({ ...acc, [val.name]: val.generate() }), {});

const setupController = async () => {
  // const DS4 = new DualShock4();
  // // This will request the WebHID device and initialize the controller
  // await DS4.init();

  // const [device] = await navigator.hid.requestDevice({
  //   // TODO: Add more compatible controllers?
  //   filters: [
  //     // Official Sony Controllers
  //     { vendorId: 0x054c, productId: 0x0ba0 },
  //     { vendorId: 0x054c, productId: 0x05c4 },
  //     { vendorId: 0x054c, productId: 0x09cc },
  //     { vendorId: 0x054c, productId: 0x05c5 },
  //   ],
  // });

  const [device] = await navigator.hid.getDevices();

  if (!device.opened) {
    device.open();

    device.addEventListener("inputreport", (event) => {
      controllerData = parseInputReport(event);
    });
  }
};

function parseMidiMessage(message) {
  return {
    command: message.data[0] >> 4,
    channel: message.data[0] & 0xf,
    note: message.data[1],
    velocity: message.data[2] / 127,
  };
}

let velocity = 0;
let lastNote = 0;

let outerUniforms = {};

function App() {
  const [uniforms, setUniforms] = useState(generateUniforms());

  useEffect(() => {
    navigator.requestMIDIAccess().then((midiAccess) => {
      midiAccess.inputs.forEach(function (entry) {
        entry.onmidimessage = (event) => {
          const message = parseMidiMessage(event);

          // console.log(message);

          if (message.command === 9 && lastNote !== message.note) {
            const uni = generateUniforms();
            setUniforms({ ...outerUniforms, spotlight_1: uni["spotlight_1"] });
            lastNote = message.note;
          }

          if (message.velocity && message.command === 11) {
            velocity += message.velocity / 20;

            if (velocity > 1) {
              velocity = 1;
            }
          }

          // midiData[message.note] = message.velocity;
          // setUniforms((uniforms) => ({
          //   ...uniforms,
          //   [properties[message.note].name]: message.velocity,
          //   // [message.note.toString()]: message.velocity,
          // }));
        };
      });
    });
  }, []);

  useEffect(() => {
    outerUniforms = uniforms;
  }, [uniforms]);

  useEffect(() => {
    // setupController();
  }, []);

  return (
    <main style={{ height: "100%" }}>
      <Canvas>
        <Suspense fallback={null}>
          <DebugContext.Provider value={false}>
            <Scene uniforms={uniforms} />
          </DebugContext.Provider>
        </Suspense>
      </Canvas>

      <UniformControls
        uniforms={uniforms}
        handleChange={(uniforms) =>
          setUniforms({
            ...uniforms,
            // depth: Math.floor(uniforms.depth),
          })
        }
      />

      {/*<MaterialData
        material={{ ...material, heightmap: null, colormap: null }}
        handleGenerate={() => setMaterial(generateMaterial())}
        handleChange={(mat) =>
          setMaterial({
            ...mat,
            heightmap: material.heightmap,
            colormap: material.colormap,
          })
        }
      />*/}
    </main>
  );
}

export default App;
